import NiceModal from '@ebay/nice-modal-react';

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as F from 'components/form/FormLayout.styled';
import Modal from 'components/global/Modal';
import NoRecordsFound from 'components/global/NoRecordsFound';
import Typography from 'components/global/Typography';
import { ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import { SHIPPING_PRICES } from 'services/consts';

import AppLogger from 'utils/AppLogger';

/**
 * @author @muhammedburakak
 */

const RecordHistory = NiceModal.create(({ invoiceID, shipmentID }) => {
	const { t } = useTranslation();
	const { showError } = useAlert();
	const axios = useAxiosPrivate();

	const [history, setHistory] = useState([]);
	const [isLoading, setIsLoading] = useState(false);

	const fillForm = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await axios.post(SHIPPING_PRICES.GET_INVOICE_DETAIL_INFO, {
				invoiceID,
				shipmentID
			});

			if (response.status === 200) {
				setHistory(response.data);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		if (invoiceID && shipmentID) {
			fillForm();
		}
	}, []);

	return (
		<Modal maxWidth="no-max-width" fullwidth className="modal-recordHistory">
			<ModalTitle>
				<Typography variant="p1">
					{t('recordHistory')} (<strong>{invoiceID}</strong>)
				</Typography>
			</ModalTitle>
			<F.TabsFormContainer>
				<ModalContent style={{ width: '100%' }}>
					<RecordHistoryTable data={history} isLoading={isLoading} />
				</ModalContent>
			</F.TabsFormContainer>
		</Modal>
	);
});

export default RecordHistory;

const RecordHistoryTable = ({ data, isLoading }) => {
	const { getLocaleDate } = useLocale();

	const columns = React.useMemo(
		() => [
			{
				header: 'invoiceTransactionID',
				accessorKey: 'invoiceTransactionID',
				size: 70
			},
			{
				header: 'recordType',
				accessorKey: 'recordType',
				size: 70,
				showOnMobile: true
			},
			{
				header: 'recordDate',
				accessorKey: 'recordDate',
				size: 30,
				renderCell: ({ row }) => <Typography>{getLocaleDate(row.original.recordDate)}</Typography>,
				showOnMobile: true
			},
			{
				header: 'recordDescription',
				accessorKey: 'recordDescription',
				size: 100
			},
			{
				header: 'correctionAmount',
				accessorKey: 'correctionAmount',
				size: 55,
				renderCell: ({ row }) => (
					<Typography>
						{row.original.correctionAmount ? row.original.correctionAmount : '-'} {row.original.currency}
					</Typography>
				)
			},
			{
				header: 'discountAmount',
				accessorKey: 'discountAmount',
				size: 50,
				renderCell: ({ row }) => (
					<Typography>
						{row.original.discountAmount ? row.original.discountAmount : '-'} {row.original.currency}
					</Typography>
				)
			},
			{
				header: 'basePrice',
				accessorKey: 'basePrice',
				size: 40,
				renderCell: ({ row }) => (
					<Typography>
						{row.original.basePrice ? row.original.basePrice : '-'} {row.original.currency}
					</Typography>
				)
			},
			{
				header: 'codFee',
				accessorKey: 'codFee',
				size: 40,
				renderCell: ({ row }) => (
					<Typography>
						{row.original.codFee ? row.original.codFee : '-'} {row.original.currency}
					</Typography>
				)
			},
			{
				header: 'extraKGFee',
				accessorKey: 'extraKGFee',
				size: 40,
				renderCell: ({ row }) => (
					<Typography>
						{row.original.extraKGFee ? row.original.extraKGFee : '-'} {row.original.currency}
					</Typography>
				)
			},
			{
				header: 'extraKMFee',
				accessorKey: 'extraKMFee',
				size: 40,
				renderCell: ({ row }) => (
					<Typography>
						{row.original.extraKMFee ? row.original.extraKMFee : '-'} {row.original.currency}
					</Typography>
				)
			},
			{
				header: 'insuranceFee',
				accessorKey: 'insuranceFee',
				size: 50,
				renderCell: ({ row }) => (
					<Typography>
						{row.original.insuranceFee ? row.original.insuranceFee : '-'} {row.original.currency}
					</Typography>
				),
				showOnMobile: true
			},
			{
				header: 'returnFee',
				accessorKey: 'returnFee',
				size: 40,
				renderCell: ({ row }) => (
					<Typography>
						{row.original.returnFee ? row.original.returnFee : '-'} {row.original.currency}
					</Typography>
				),
				showOnMobile: true
			}
		],
		[]
	);

	return (
		<Table
			columns={columns}
			data={data}
			loading={isLoading}
			noRecordsFound={
				<NoRecordsFound
					lottieName="no-items"
					height="10rem"
					title="noRecordHistory"
					subTitle="noRecordHistorySubtitle"
				/>
			}
		/>
	);
};
